import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { pitchType, ToggleButtons } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import StrikeZone from './StrikeZone';
import { usePitchVideoFn } from '../data';

const StrikeZoneWrapper = styled.div`
  && {
     & [data-highcharts-chart] {
    overflow: visible !important;
  }

  & .highcharts-container {
    overflow: visible !important;
  }

  & .highcharts-root {
    overflow: visible !important;
  }
  }
`;

const StyledToggleButtons = styled(ToggleButtons)`
    z-index: 2;
`;

const plotPitches = (translate, pitches, passedballs, pbState, pbHandler, usesAbs) => {
    const series = !usesAbs
        ? [
            { data: [], name: `+0.5 to +1.0 (${translate('baseball.strikes_other')})`, index: 1, id: 'gained', color: '#3366ff', marker: { radius: 4 } },
            { data: [], name: `+0.3 to +0.5 (${translate('baseball.strikes_other')})`, index: 2, id: 'expstrike', color: '#7BADF9', marker: { radius: 3 } },
            { data: [], name: `-0.5 to -0.3 (${translate('baseball.balls_other')})`, index: 3, id: 'expball', color: '#F08C89', marker: { radius: 3 } },
            { data: [], name: `-1.0 to -0.5 (${translate('baseball.balls_other')})`, index: 4, id: 'lost', color: '#bf0b23', marker: { radius: 4 } },
            {
                data: [],
                name: translate('components.receiving-chart.passed-ball'),
                index: 5,
                id: 'pb',
                color: '#ff9944',
                marker: { symbol: 'square', radius: 3 },
                events: {
                    legendItemClick() {
                        pbHandler(!pbState);
                        this.update({ visible: !pbState });
                    },
                },
                visible: pbState,
            },
        ] : [
            { data: [], name: '', index: 6, id: 'abs', color: '#000', marker: { radius: 3 } },
            {
                data: [],
                name: translate('components.receiving-chart.passed-ball'),
                index: 5,
                id: 'pb',
                color: '#ff9944',
                marker: { symbol: 'square', radius: 3 },
                events: {
                    legendItemClick() {
                        pbHandler(!pbState);
                        this.update({ visible: !pbState });
                    },
                },
                visible: pbState,
            },
        ];
    pitches.forEach((pitch) => {
        if (pitch.platex_adj) {
            const tooltip = [
                `${translate('components.receiving-chart.strike-prob', { value: (pitch.pstrike * 100).toFixed(1) })}`,
                `${translate('components.receiving-chart.called')} ${(pitch.isstrike ? translate('baseball.strikes_one') : translate('baseball.balls_one'))}`,
                `${translate('components.receiving-chart.pitch-type')}: ${pitchType(pitch.type, true, undefined, translate)}`,
                `${(pitch.islhb ? `${translate('baseball.lhh')}  - ` : `${translate('baseball.rhh')} - `)} ${pitch.batter}`,
                `${(pitch.islhp ? `${translate('baseball.lhp')} - ` : `${translate('baseball.rhp')} - `)} ${pitch.pitcher}`,
            ].join('<br/>');
            const point = {
                x: pitch.platex_adj * -1,
                y: pitch.platez_adj,
                // eslint-disable-next-line no-nested-ternary
                name: pitch.gained ? translate('components.receiving-chart.gained') : pitch.lost ? translate('components.receiving-chart.lost') : translate('components.receiving-chart.expected'),
                tooltip,
                url: pitch.url,
                gameid: pitch.gameid,
                eventseq: pitch.eventseq,
                pitchseq: pitch.pitchseq,
                id: `${pitch.gameid}_${pitch.eventseq}_${pitch.pitchseq}`,
            };

            if (usesAbs && pitch.usesabs) {
                series[0].data.push(point);
                return;
            }
            if (!usesAbs) {
                if (pitch.gained) series[0].data.push(point);
                else if (!pitch.gained && !pitch.lost && pitch.isstrike === true) series[1].data.push(point);
                else if (!pitch.gained && !pitch.lost && pitch.isstrike === false) series[2].data.push(point);
                else if (pitch.lost) series[3].data.push(point);
            }
        }
    });

    passedballs.forEach((passedball) => {
        const tooltip = [
            `${translate('components.receiving-chart.passed-ball')}`,
            `${translate('components.receiving-chart.pitch-type')}: ${pitchType(passedball.type, true, undefined, translate)}`,
            `${(passedball.islhb ? `${translate('baseball.lhh')}  - ` : `${translate('baseball.rhh')} - `)} ${passedball.batter}`,
            `${(passedball.islhp ? `${translate('baseball.lhp')} - ` : `${translate('baseball.rhp')} - `)} ${passedball.pitcher}`,
        ].join('<br/>');

        const point = {
            x: passedball.platex_adj * -1,
            y: passedball.platez_adj,
            name: translate('components.receiving-chart.passed-ball'),
            tooltip,
            gameid: passedball.gameid,
            eventseq: passedball.eventseq,
            pitchseq: passedball.pitchseq,
            id: `${passedball.gameid}_${passedball.eventseq}_${passedball.pitchseq}`,
        };
        if (usesAbs) {
            series[1].data.push(point);
            return;
        }
        if (!usesAbs) {
            series[4].data.push(point);
        }
    });

    return series;
};

const VIEW = { ALL: 'all', LHH: 'lhh', RHH: 'rhh' };

const ReceivingChart = ({ pitches, passedBalls, isGameData }) => {
    const { t: translate } = useTranslation();
    const [view, setView] = useState(VIEW.ALL);
    const [passedBallState, setPassedBallState] = useState(false);
    const [usesAbs] = useState(pitches.filter((p) => p.usesabs).length > 0);
    const options = [
        { name: translate('common.all'), value: VIEW.ALL, variant: 'default' },
        { name: translate('baseball.lhh'), value: VIEW.LHH, variant: 'default' },
        { name: translate('baseball.rhh'), value: VIEW.RHH, variant: 'default' },
    ];

    const filteredPitchlog = pitches.filter((pitch) => (view === VIEW.LHH && pitch.lhh) || (view === VIEW.RHH && pitch.rhh) || view === VIEW.ALL);
    const filteredPassedBalls = passedBalls.filter((pb) => (view === VIEW.LHH && pb.islhb) || (view === VIEW.RHH && !pb.islhb) || view === VIEW.ALL);
    const seriesData = plotPitches(translate, filteredPitchlog, filteredPassedBalls, passedBallState, setPassedBallState, usesAbs);
    const pitchVideoFn = usePitchVideoFn();
    const customLegend = {
        align: 'right',
        verticalAlign: 'top',
        layout: 'vertical',
        floating: true,
        x: 100,
        y: -10,
        itemStyle: {
            fontSize: '10px',
        },
    };

    return (
        <>
            <StyledToggleButtons name="receiving" size="sm" options={options} onChange={(value) => setView(value)} defaultValue={VIEW.ALL} />
            <StrikeZoneWrapper>
                <StrikeZone
                    enableZoom={false}
                    showlegend={!usesAbs}
                    customLegend={customLegend}
                    legendposition="below"
                    seriesData={seriesData}
                    showbatter={view !== VIEW.ALL}
                    showlhb={view === VIEW.LHH}
                    pitchVideoFn={pitchVideoFn}
                    useABSRulebookZone={isGameData}
                    useBaseballMarkers={isGameData}
                />
            </StrikeZoneWrapper>
        </>
    );
};

ReceivingChart.propTypes = {
    pitches: PropTypes.arrayOf(PropTypes.object).isRequired,
    passedBalls: PropTypes.arrayOf(PropTypes.object).isRequired,
    isGameData: PropTypes.bool,
};

ReceivingChart.defaultProps = {
    isGameData: false,
};

export default ReceivingChart;
